import React, { useState } from "react";
import { INK, MUTED, GRID, W, M, axis, scale, plural, clusterColor, Halo, Legend, Tooltip } from "./clusterKit";
import ModeWord from "./ModeWord";

// ---- silhouette_plot: every case's silhouette width, one thin bar each, sorted within its cluster ----
// Props (ChartData#silhouette_plot): clusters [{ name, n, widths [sorted, largest first], mean }], average (everyone's),
// negative (how many below 0), lowest, highlight { row, cluster, width, rank } (one case to mark), legendTitle, noun.
const bare = (v, places = 2) => (v < 0 ? "−" : "") + Math.abs(v).toFixed(places).replace(/^0(?=\.)/, "");
const tick = (t) => (t === 0 ? "0" : bare(t, 1));
const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function SilhouettePlotChart({ clusters, average, negative, lowest, highlight, legendTitle, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const total = clusters.reduce((a, c) => a + c.n, 0);
	const bar = Math.max(1, Math.min(6, 330 / total)), gap = 20;
	const top = M.top + 26, plotH = total * bar + gap * (clusters.length - 1);
	const height = top + plotH + 44;
	const box = { l: 24, r: W - 176, t: top, b: top + plotH };
	const highest = Math.max(...clusters.flatMap((c) => c.widths));
	const axX = axis([Math.min(0, lowest), highest], 0.04);
	const x = scale(axX, box.l, box.r);
	let offset = top;
	const blocks = clusters.map((c, i) => {
		const block = { ...c, i, y0: offset, y1: offset + c.n * bar };
		offset = block.y1 + gap;
		return block;
	});
	const best = clusters.reduce((b, c, i) => (c.mean > clusters[b].mean ? i : b), 0);
	const below = blocks.map((b) => b.widths.filter((w) => w < 0).length);
	const mark = highlight && blocks[highlight.cluster];
	const markY = mark && mark.y0 + highlight.rank * bar + bar / 2;
	return (
		<figure>
			<Legend title={legendTitle} items={[...clusters.map((c, i) => ({ label: c.name, color: clusterColor(i), kind: "square" })),
				{ label: `Average for everyone, s̄ = ${bare(average)}`, color: INK, kind: "dash" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Silhouette plot of ${plural(total, noun)}, one bar each, sorted within cluster. ${clusters.map((c) => `${c.name}: ${plural(c.n, noun)}, average ${bare(c.mean)}`).join("; ")}. Average for everyone ${bare(average)}. ${negative ? `Below 0: ${plural(negative, noun)}, the lowest ${bare(lowest)}.` : `No ${noun} has a width below 0.`}`}>
				{axX.ticks.map((t) => (
					<g key={t}>
						<line x1={x(t)} x2={x(t)} y1={box.t - 4} y2={box.b + 4} stroke={t === 0 ? MUTED : GRID} strokeWidth={t === 0 ? 1.5 : 1} />
						<text x={x(t)} y={box.b + 18} fontSize="11" fill={MUTED} textAnchor="middle">{tick(t)}</text>
					</g>
				))}
				<text x={box.r} y={box.b + 34} fontSize="11" fill={MUTED} textAnchor="end">Silhouette width (s): below 0, closer to another cluster than to its own</text>
				{blocks.map((b) => {
					const dim = hover !== null && hover !== b.i;
					return (
						<g key={b.name} opacity={dim ? 0.35 : 1} onMouseEnter={() => setHover(b.i)} onMouseLeave={() => setHover(null)}>
							<rect x={box.l} y={b.y0} width={box.r - box.l} height={b.y1 - b.y0} fill="transparent" />
							{b.widths.map((w, j) => (
								<rect key={j} x={x(Math.min(0, w))} y={b.y0 + j * bar} width={Math.max(0.5, Math.abs(x(w) - x(0)))} height={bar + 0.35}
									fill={clusterColor(b.i)} pointerEvents="none" />
							))}
							<text x={box.r + 10} y={(b.y0 + b.y1) / 2 - 8} fontSize="12" fill={INK} fontWeight="600">{b.name}</text>
							<text x={box.r + 10} y={(b.y0 + b.y1) / 2 + 7} fontSize="11" fill={MUTED}>{plural(b.n, noun)}, s̄ = {bare(b.mean)}</text>
							{below[b.i] > 0 && (
								<Halo x={x(0) + 6} y={b.y1 + 13} fill={INK} fontSize="10.5" pointerEvents="none">
									↖ {plural(below[b.i], noun)} below 0
								</Halo>
							)}
						</g>
					);
				})}
				<line x1={x(average)} x2={x(average)} y1={box.t - 8} y2={box.b + 4} stroke={INK} strokeWidth="1.5" strokeDasharray="6 4" pointerEvents="none" />
				<Halo x={x(average) + 5} y={box.t - 10} fill={INK} fontWeight="600" pointerEvents="none">s̄ = {bare(average)}</Halo>
				{mark && (
					<g pointerEvents="none">
						<rect x={x(Math.min(0, highlight.width))} y={markY - 1.5} width={Math.abs(x(highlight.width) - x(0))} height="3" fill={INK} />
						<Halo x={x(Math.max(0, highlight.width)) + 6} y={markY + 4} fill={INK} fontWeight="600">
							{capital(noun)} {highlight.row}: s = {bare(highlight.width)}
						</Halo>
					</g>
				)}
				{hover !== null && (
					<Tooltip x={x(0) + 60} lines={[`${clusters[hover].name}: ${plural(clusters[hover].n, noun)}`, `Average width ${bare(clusters[hover].mean)}`,
						`From ${bare(clusters[hover].widths[0])} down to ${bare(clusters[hover].widths[clusters[hover].n - 1])}`,
						below[hover] ? `${capital(plural(below[hover], noun))} below 0` : "None below 0"]} />
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each thin bar is one {noun}'s silhouette width: how much closer they are to their own cluster than to the next nearest one, from −1 to 1. Within each cluster the bars are sorted, longest first, so a cluster that stays wide all the way down is a well-separated one; here that's {clusters[best].name} (s̄ = {bare(clusters[best].mean)}).
				{" "}The dashed line is the average for everyone, s̄ = {bare(average)}.
				{negative > 0 ? ` Left of 0 ${negative === 1 ? "is" : "are"} ${plural(negative, noun)} closer on average to another cluster than to their own; short bars like these mark ${noun}s who sit between profiles.` : ""}
				{mark ? <>{" "}The <ModeWord light="black" dark="white" /> bar is {`${noun} ${highlight.row}, s = ${bare(highlight.width)}${highlight.rank >= 0.75 * mark.n ? `, near the bottom of the ${mark.name} cluster: in it, but only just` : ""}.`}</> : ""}
			</figcaption>
		</figure>
	);
}
